import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IoClose } from "react-icons/io5";
import { IoChevronDown } from "react-icons/io5";
import debounce from 'lodash/debounce';

interface NotepadProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (note: { title: string; description: string; status: string }) => Promise<boolean>;
}

const statusOptions = [
  { label: 'New lead', color: '#EB1212' },
  { label: 'In progress', color: '#ABBA09' },
  { label: 'Negotiation', color: '#F57F17' },
  { label: 'Closed-won', color: '#388E3C' },
  { label: 'Closed-lost', color: '#121AEB' }
];

const additionalColors = [
  '#FF6B6B', // Coral Red
  '#4ECDC4', // Turquoise
  '#45B7D1', // Sky Blue
  '#96CEB4', // Sage Green
  '#FF8C42', // Orange
  '#D4A5A5', // Dusty Rose
  '#9B59B6', // Purple
  '#3498DB', // Blue
  '#E67E22', // Carrot Orange
  '#1ABC9C'  // Emerald
];

const Notepad: React.FC<NotepadProps> = ({ isOpen, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [newStatusInput, setNewStatusInput] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [statusOptions, setStatusOptions] = useState([
    { label: 'New lead', color: '#EB1212' },
    { label: 'In progress', color: '#ABBA09' },
    { label: 'Negotiation', color: '#F57F17' },
    { label: 'Closed-won', color: '#388E3C' },
    { label: 'Closed-lost', color: '#121AEB' }
  ]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    
    setIsSaving(true);
    try {
      const success = await onSave({
        title: title.trim(),
        description: description.trim(),
        status: status || 'In progress',
      });
      
      if (success) {
        setTitle('');
        setDescription('');
        setStatus('');
        onClose();
      }
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    setIsStatusDropdownOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 bg-white rounded-lg z-50 text-left">
      {/* Header */}
      <div className="flex justify-between items-center p-[2px]">
        <h2 className="text-[20px] font-semibold text-[#222222]">Create notes</h2>
        <button 
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <IoClose className="w-4 h-4 text-[#75777C]" />
        </button>
      </div>

      {/* Form */}
      <div className="flex flex-col gap-4 pt-[18px]">
        <div className="space-y-1">
          <label className="text-[14px] text-[#222222]">Name</label>
          <input
            type="text"
            placeholder="Give your note a name"
            value={title}
            onChange={handleTitleChange}
            className="w-full px-4 py-[10px] border border-[#EEEEEE] rounded-lg text-[14px] focus:outline-none focus:border-[#29DC5B]"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[14px] text-[#222222]">Description (optional)</label>
          <textarea
            placeholder="Write your description"
            value={description}
            onChange={handleDescriptionChange}
            className="w-full h-[68px] px-4 py-[10px] border border-[#EEEEEE] rounded-lg text-[14px] resize-none focus:outline-none focus:border-[#29DC5B]"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[14px] text-[#222222]">Status (optional)</label>
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className="w-full px-4 py-[10px] border border-[#EEEEEE] rounded-lg text-[14px] text-left flex justify-between items-center focus:border-[#29DC5B]"
            >
              {status ? (
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: statusOptions.find(opt => opt.label === status)?.color }}
                  />
                  <span className="text-[#222222]">{status}</span>
                </div>
              ) : (
                <span className="text-[#9FA1A7]">Select status</span>
              )}
              <IoChevronDown className="w-4 h-4 text-[#75777C]" />
            </button>

            {isStatusDropdownOpen && (
              <div className="absolute top-full left-0 w-full mt-1 bg-[#FFFFFF] border border-[#EEEEEE] rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto scrollbar-hide">
                {statusOptions.map((option) => (
                  <div
                    key={option.label}
                    onClick={() => {
                      handleStatusChange(option.label);
                    }}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-[#FAFAFA] cursor-pointer"
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                    <span className="text-[14px]">{option.label}</span>
                  </div>
                ))}
                
                {/* Divider */}
                <div className="h-[1px] bg-[#EEEEEE] mx-3" />
                
                {/* Add custom status button */}
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Enter new status"
                    value={newStatusInput}
                    onChange={(e) => setNewStatusInput(e.target.value)}
                    className="w-full px-2 py-1 text-[12px] border border-[#EEEEEE] rounded-lg focus:outline-none focus:border-[#29DC5B]"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  <div className="flex flex-wrap gap-2 my-2">
                    {additionalColors.map((color) => (
                      <button
                        key={color}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedColor(color);
                        }}
                        className={`w-3 h-3 rounded-full border ${
                          selectedColor === color ? 'border-[#000000]' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (newStatusInput.trim() && selectedColor) {
                        const newStatus = {
                          label: newStatusInput.trim(),
                          color: selectedColor
                        };
                        setStatusOptions([...statusOptions, newStatus]);
                        setStatus(newStatusInput.trim());
                        setNewStatusInput('');
                        setSelectedColor('');
                        setIsStatusDropdownOpen(false);
                      }
                    }}
                    className="flex items-center w-full justify-center gap-1.5 px-2 py-1 mb-2 text-[#000000] bg-[#FFFFFF] rounded-[6px] hover:bg-[#FAFAFA] border border-[#EEEEEE] disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!newStatusInput.trim() || !selectedColor}
                  >
                    <span className="text-[12px] font-medium ">Add Status</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Save button at the bottom */}
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || isSaving}
            className="flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all hover:bg-[#FAFAFA] border border-[#EEEEEE] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span className="w-[14px] h-[14px] border-2 border-[#7A7A7A] border-t-transparent rounded-full animate-spin"/>
                <span className="text-[14px] font-[400]">Saving...</span>
              </>
            ) : (
              <>
                <svg className="w-[14px] h-[14px] text-[#7A7A7A]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 21H19C20.1046 21 21 20.1046 21 19V8.82843C21 8.29799 20.7893 7.78929 20.4142 7.41421L16.5858 3.58579C16.2107 3.21071 15.702 3 15.1716 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 3V8H15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 21V15H17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-[14px] font-[400]">Save Note</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notepad;
